"use client";
import { useSite } from "@/lib/hooks/site";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useHeader } from "@/hooks/headerContext";
import { useVenueListPage } from "@/hooks/listPageContext";
import { ROUTES } from "@/@core/constants/routes";
import { ReactNode, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "@/hooks/translationContext";
import HeaderLocationSearchDescription from "@/components/views/common/description/SearchResultDescription";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import { useViewMode } from "@/context/ViewModeContext";
import { cn } from "@/lib/utils";
import { Link } from "@/components/CustomLink";
import BuildingStorefrontIcon from "@heroicons/react/24/outline/BuildingStorefrontIcon";
import TicketIcon from "@heroicons/react/24/outline/TicketIcon";
import ReceiptPercentIcon from "@heroicons/react/24/outline/ReceiptPercentIcon";
import ChatBubbleLeftEllipsisIcon from "@heroicons/react/24/outline/ChatBubbleLeftEllipsisIcon";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import CalendarDaysIcon from "@heroicons/react/24/outline/CalendarDaysIcon";
import { useNearbyNavigation } from "@/hooks/useNearbyNavigation";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import toast from "react-hot-toast";

interface Props {
  customTitle?: string;
  showMenu?: boolean;
  showTitleAllTime?: boolean;
  headerLocationComponent: ReactNode;
  quoteButtonComponent: ReactNode;
}

export default function NavigationHeaderClient(props: Props) {
  const {
    headerLocationComponent,
    quoteButtonComponent,
    showMenu = true,
  } = props;
  const site = useSite();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { district, service, dataCount } = useHeader();
  const path = usePathname();
  const { t } = useTranslation();
  const { isVenueListPage } = useVenueListPage();
  const deviceContext = useCurrentDeviceContext();
  const isMobile = deviceContext.device !== "desktop";
  const { viewMode, setViewMode } = useViewMode();
  const { goToNearbyList } = useNearbyNavigation();

  // Mobile sticky behavior: track if we should be fixed or in normal flow
  const navRef = useRef<HTMLElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [navHeight, setNavHeight] = useState(0);

  // Get initial offset position and handle scroll for mobile sticky behavior
  useEffect(() => {
    if (deviceContext.device === "desktop") return;

    const nav = navRef.current;
    if (!nav) return;

    // Store the nav height for placeholder
    setNavHeight(nav.offsetHeight);

    // Store the initial offset top when component mounts
    const initialOffset = nav.offsetTop;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsSticky(scrollY >= initialOffset);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener("scroll", handleScroll);
  }, [deviceContext.device]);

  const getHrefLink = () => {
    if (service?.id && district?.id) {
      return `/${district?.slug}-${service?.slug}`;
    }

    if (!service?.id && district?.id) {
      return `/${district?.slug}-${site.setting?.routes?.districtSpecificList}`;
    }

    if (service?.id && !district?.id) {
      return `/${service?.slug}`;
    }

    return `/${site?.setting?.routes?.generalList}`;
  };

  const getMenuHrefLink = (href: string) => {
    const venue = searchParams.get("venue");

    if (href == site.setting?.routes?.generalList) {
      if (service?.id && district?.id) {
        return `/${district?.slug}-${service?.slug}`;
      }

      if (!service?.id && district?.id) {
        return `/${district?.slug}-${site.setting?.routes?.districtSpecificList}`;
      }

      if (service?.id && !district?.id) {
        return `/${service?.slug}`;
      }
    }

    const url = new URLSearchParams();
    if (service?.id) url.append("service", service?.slug);
    if (district?.id) url.append("district", district.slug);
    if (venue) url.append("venue", venue);

    const queryString = url.toString();
    return queryString ? `${href}?${queryString}` : href;
  };

  const getMenu = () => {
    const isPageButtonsArray = Array.isArray(
      site?.setting?.pageButtons?.generalList
    );

    const venueLabel = t("venue-header-title", {
      defaultValue: "Mekanlar",
    });

    return [
      {
        label: venueLabel,
        key: "venue",
        icon: BuildingStorefrontIcon,
        count: dataCount?.venues || 0,
        onClick: () => {
          if (isVenueListPage && viewMode == "map") {
            setViewMode("list");
          } else {
            const href = getHrefLink();
            router.push(href);
          }
        },
        active: viewMode == "list" && isVenueListPage,
        isHidden: false,
        enabled: true,
      },
      {
        label: t("company-header-title", { defaultValue: "Şirket İndirimleri" }),
        key: "company",
        icon: ReceiptPercentIcon,
        count: dataCount?.companies || 0,
        path: ROUTES.ALL_COMPANIES,
        active: path === ROUTES.ALL_COMPANIES,
        isHidden: site?.setting?.disabledPages?.company === 1,
        enabled:
          site?.setting?.pageButtons?.generalList?.includes("companyDiscounts"),
      },
      {
        label: "Kampanyalar",
        key: "campaigns",
        icon: TicketIcon,
        count: dataCount?.campaigns + dataCount?.nearby.campaigns || 0,
        path: ROUTES.CAMPAIGNS,
        active: path === ROUTES.CAMPAIGNS,
        isHidden: site?.setting?.disabledPages?.campaign === 1,
        enabled: site?.setting?.pageButtons?.generalList?.includes("campaign"),
      },
      {
        label: "Etkinlikler",
        key: "events",
        icon: CalendarDaysIcon,
        count: dataCount?.events || 0,
        path: ROUTES.EVENTS,
        active: path === ROUTES.EVENTS,
        isHidden: site?.setting?.disabledPages?.event === 1,
        enabled: site?.setting?.pageButtons?.generalList?.includes("event"),
      },
      {
        label: "Soru Sor",
        key: "question",
        icon: ChatBubbleLeftEllipsisIcon,
        onClick: () => {
          const target =
            document.getElementById("comment-form") ||
            document.getElementById("list-comment-section") ||
            document.getElementById("campaign-comment-section") ||
            document.getElementById("event-comment-section") ||
            document.getElementById("company-discount-comment-section");
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        },
        active: false,
        isHidden: !isAskQuestionEnabled,
        enabled: true,
      },
      {
        label: "Yakındakiler",
        key: "nearby",
        icon: MapPinIcon,
        onClick: () => {
          toast.success(
            t("header-nearby-sorted-info", { venueLabel }),
            { duration: 4000 }
          );
          goToNearbyList();
        },
        active: false,
        isHidden: false,
        enabled: true,
      },
    ].filter(
      (m) =>
        !m.isHidden &&
        (isPageButtonsArray ? m.enabled : true) &&
        (m.key === "map" || m.key === "question" || m.key === "nearby" || m.count > 0)
    );
  };

  const menu = useMemo(
    () => getMenu(),
    [viewMode, site.setting, dataCount, isVenueListPage, path]
  );

  useEffect(() => {
    if (
      pathName.includes(ROUTES.EVENTS) &&
      site?.setting?.disabledPages?.event == 1
    )
      router.push("/404");
    if (
      pathName.includes(ROUTES.CAMPAIGNS) &&
      site?.setting?.disabledPages?.campaign == 1
    )
      router.push("/404");
    if (
      pathName.includes(ROUTES.ALL_COMPANIES) &&
      site?.setting?.disabledPages?.company == 1
    )
      router.push("/404");
  }, [pathName]);

  function showCustomTitle() {
    if (!props.customTitle) return null;
    if (!props.showTitleAllTime && (district?.id || service?.id)) return null;

    return (
      <h1
        className="font-bold text-[19.7px] text-[#212a3e] tracking-[1px] leading-[30px]"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {props.customTitle}
      </h1>
    );
  }

  const commonClassName = viewMode === "map" ? "hidden" : "";

  // Render menu item
  const renderMenuItem = (item: (typeof menu)[0], index: number) => {
    const IconComponent = item.icon;
    const isActive = item.active;

    const content = (
      <div className="flex flex-col items-center gap-0.5 px-0.5 py-0.5 relative w-full max-md:min-w-0">
        <IconComponent
          className={cn(
            "size-6 shrink-0",
            isActive ? "text-[#cd0241]" : "text-black"
          )}
        />
        <span
          className={cn(
            "text-[12px] max-md:text-[10px] font-medium tracking-[-0.12px] text-center break-words leading-tight md:whitespace-nowrap",
            isActive ? "text-[#cd0241]" : "text-black"
          )}
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {item.label}
        </span>
      </div>
    );

    if (item.path) {
      const href = item.path.startsWith("#")
        ? item.path
        : getMenuHrefLink(item.path);
      return (
        <Link
          title={item.label}
          href={href}
          key={index}
          className="max-md:flex-1 max-md:min-w-0 flex justify-center"
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        role="button"
        key={index}
        onClick={item.onClick}
        className="max-md:flex-1 max-md:min-w-0 flex justify-center"
      >
        {content}
      </button>
    );
  };

  return (
    <>
      {showMenu && (
        <nav
          ref={navRef}
          className={cn(
            "bg-white z-50 w-full",
            // Mobile: normal flow by default, fixed when scrolled past
            isSticky ? "max-md:fixed max-md:top-0" : "max-md:relative",
            // Desktop: always sticky at top
            "md:sticky md:top-0",
            commonClassName,
            !menu || (menu.length === 0 && "max-md:hidden")
          )}
        >
          {/* Container - constrained by max-width */}
          <div className="container">
            {/* Single navigation block for every breakpoint: the menu list is
                rendered once and only its layout changes. Title/location and the
                quote button live here on desktop; on mobile they are rendered by
                the title nav below, so they are skipped instead of duplicated. */}
            <div className="border-b border-[#d9d9d9]">
              <div className="flex items-center justify-between md:h-[72px]">
                {!isMobile && (
                  /* Left: Title */
                  <div className="flex flex-col gap-4">
                    {showCustomTitle()}
                    {headerLocationComponent}
                  </div>
                )}

                {/* Menu Items - evenly spread on mobile, inline on desktop */}
                <div className="flex gap-6 md:items-center max-md:w-full max-md:items-start max-md:justify-between max-md:gap-0.5 max-md:px-1 max-md:pt-1 max-md:pb-2">
                  {menu.map((item, index) => renderMenuItem(item, index))}
                </div>

                {!isMobile && (
                  /* Right: Quote Button */
                  <div className="flex items-center gap-4">
                    {quoteButtonComponent}
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Placeholder to prevent content jump when nav becomes fixed on mobile */}
      {showMenu && isSticky && isMobile && (
        <div style={{ height: navHeight }} aria-hidden="true" />
      )}

      {!showMenu && (
        <nav className={cn("w-full border-b border-[#d9d9d9]", commonClassName)}>
          <div className="container flex justify-between flex-wrap items-center w-full gap-1 py-[20px]">
            <div>
              <h1
                className="font-bold text-[19.7px] text-[#212a3e] tracking-[1px]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {props.customTitle
                  ? props.customTitle
                  : headerLocationComponent}
              </h1>
            </div>

            <div className="flex flex-wrap items-center sm:gap-4 gap-2 w-auto">
              {quoteButtonComponent}
            </div>
          </div>
        </nav>
      )}

      {/* Mobile: Title Section (Breadcrumb is now rendered separately above navigation) */}
      {showMenu && isMobile && (
        <nav
          className={cn("flex flex-col container py-2", commonClassName)}
        >
          {showCustomTitle()}

          <div className="flex gap-2 items-center">
            {headerLocationComponent}
          </div>
        </nav>
      )}

      <div className={viewMode === "map" ? "hidden" : ""}>
        {showMenu && <HeaderLocationSearchDescription />}
      </div>
    </>
  );
}
